/**
 * Boletos: todas as cobranças emitidas, de qualquer lançamento, num lugar so.
 * O painel por linha e o mesmo do Financeiro (BoletoDialog) — quem opera
 * boleto nao precisa saber em que lancamento ele nasceu.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Printer, Receipt } from 'lucide-react';
import { useState } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

import {
  BoletoDialog,
  STATUS_COBRANCA,
  abrirBoletoPdf,
  type ChargeStatus,
} from './financial/BoletoDialog';

interface ChargeRow {
  id: string;
  providerName: string;
  providerChargeId?: string | null;
  status: ChargeStatus;
  valor: string | number;
  dataVencimento: string;
  dataPagamento?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  bankAccount: { name: string };
  financialEntry: {
    id: string;
    referencia: string;
    valor: string | number;
    dataVencimento: string;
    status: string;
    client?: { id: string; razaoSocial: string } | null;
  };
}

interface ListResponse {
  rows: ChargeRow[];
  total: number;
}

const FILTROS: { value: '' | ChargeStatus; label: string }[] = [
  { value: 'REGISTERED', label: 'Em aberto' },
  { value: 'PAID', label: 'Pagos' },
  { value: 'CANCELLED', label: 'Baixados' },
  { value: 'DRAFT', label: 'Recusados' },
  { value: '', label: 'Todos' },
];

function fmtData(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

function primeiroDiaDoMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export function BoletosPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const qc = useQueryClient();

  const [status, setStatus] = useState<'' | ChargeStatus>('REGISTERED');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [aberto, setAberto] = useState<ChargeRow | null>(null);

  const params = new URLSearchParams({ pageSize: '200' });
  if (status) params.set('status', status);
  if (from) params.set('from', from);
  if (to) params.set('to', to);

  const lista = useQuery({
    queryKey: ['billing', 'charges', { status, from, to }],
    queryFn: () => apiFetch<ListResponse>(`/billing/charges?${params}`),
  });

  const rows = lista.data?.rows ?? [];
  const totalValor = rows.reduce((s, r) => s + Number(r.valor), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Boletos</h1>
          <p className="text-sm text-muted-foreground">
            Tudo que foi emitido pelos bancos. Clique numa linha para consultar, prorrogar, reemitir
            ou baixar.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex rounded-md border border-border p-0.5">
          {FILTROS.map((f) => (
            <button
              key={f.value || 'todos'}
              type="button"
              onClick={() => setStatus(f.value)}
              className={`rounded px-3 py-1.5 text-sm ${
                status === f.value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <label className="block space-y-1">
          <span className="text-xs font-medium">Vencimento de</span>
          <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium">até</span>
          <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button
          type="button"
          onClick={() => {
            setFrom(primeiroDiaDoMes());
            setTo('');
          }}
          className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent"
        >
          Este mês
        </button>
        {(from || to) && (
          <button
            type="button"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          >
            limpar período
          </button>
        )}
        <span className="ml-auto text-sm text-muted-foreground">
          {lista.data ? `${lista.data.total} boleto(s) · ${formatBRL(totalValor)}` : ''}
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        {lista.isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {lista.isError && (
          <p className="p-4 text-sm text-destructive">Não foi possível carregar os boletos.</p>
        )}
        {lista.data && rows.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-10 text-sm text-muted-foreground">
            <Receipt className="h-8 w-8 opacity-40" />
            Nenhum boleto neste filtro. Boletos nascem no Financeiro, em <b>Gerar cobrança</b>.
          </div>
        )}
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Vencimento</th>
                <th className="px-4 py-2">Cliente</th>
                <th className="px-4 py-2">Referência</th>
                <th className="px-4 py-2">Banco</th>
                <th className="px-4 py-2">Nosso nº</th>
                <th className="px-4 py-2 text-right">Valor</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setAberto(r)}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-accent/30"
                >
                  <td className="px-4 py-2 text-xs">{fmtData(r.dataVencimento)}</td>
                  <td className="px-4 py-2 text-xs font-medium">
                    {r.financialEntry.client?.razaoSocial ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{r.financialEntry.referencia}</td>
                  <td className="px-4 py-2 text-xs">{r.bankAccount.name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.providerChargeId ?? '—'}</td>
                  <td className="px-4 py-2 text-right font-medium">{formatBRL(Number(r.valor))}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs ${STATUS_COBRANCA[r.status].cls}`}
                    >
                      {STATUS_COBRANCA[r.status].label}
                    </span>
                    {r.status === 'PAID' && r.dataPagamento && (
                      <div className="text-[11px] text-muted-foreground">pago em {fmtData(r.dataPagamento)}</div>
                    )}
                    {r.errorMessage && (
                      <div className="max-w-xs truncate text-[11px] text-destructive" title={r.errorMessage}>
                        {r.errorMessage}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-right">
                    {(r.status === 'REGISTERED' || r.status === 'PAID') && (
                      <button
                        type="button"
                        data-dica="Abrir PDF para imprimir"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          void abrirBoletoPdf(r.id);
                        }}
                        className="rounded-md p-2 text-primary hover:bg-primary/10"
                      >
                        <Printer className="h-4 w-4" />
                      </button>
                    )}
                    <FileText className="inline h-4 w-4 text-muted-foreground" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {aberto && (
        <BoletoDialog
          entry={aberto.financialEntry}
          canManage={canManage}
          onClose={() => setAberto(null)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ['billing'] });
            qc.invalidateQueries({ queryKey: ['financial'] });
          }}
        />
      )}
    </div>
  );
}
